import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fleetService, vehicleImageUrl } from "../services/fleetService";
import WeekGrid, { startOfWeek } from "../components/fleet/WeekGrid";
import ReservationFormModal from "../components/fleet/ReservationFormModal";
import { useI18n } from "../providers/I18nProvider";
import { useAuth } from "../contexts/AuthContext";
import "../styles/Fleet.css";

const pad = (n) => String(n).padStart(2, "0");

const formatWeekLabel = (ws) => {
  const we = new Date(ws);
  we.setDate(we.getDate() + 6);
  return `${pad(ws.getDate())}.${pad(ws.getMonth() + 1)} – ${pad(
    we.getDate()
  )}.${pad(we.getMonth() + 1)}.${we.getFullYear()}`;
};

const VehicleDetailPage = () => {
  const { id } = useParams();
  const vehicleId = Number(id);
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user: currentUser } = useAuth();
  const [vehicle, setVehicle] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [modalOpen, setModalOpen] = useState(false);
  const [initialStart, setInitialStart] = useState(null);
  const [error, setError] = useState("");

  const requestSeq = useRef(0);

  const loadData = useCallback(async () => {
    // Hızlı hafta değişiminde eski cevabın yeni veriyi ezmesini engelle
    const seq = (requestSeq.current += 1);
    try {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);
      const data = await fleetService.getVehicle(
        vehicleId,
        weekStart.toISOString(),
        weekEnd.toISOString()
      );
      if (seq !== requestSeq.current) return;
      setVehicle(data.vehicle);
      setReservations(Array.isArray(data.reservations) ? data.reservations : []);
      setError("");
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err.response?.data?.msg || t("fleet.loadError"));
    }
  }, [vehicleId, weekStart, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onSlotClick = (date) => {
    setInitialStart(date);
    setModalOpen(true);
  };

  const nextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
  };
  const prevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
  };
  const thisWeek = () => setWeekStart(startOfWeek(new Date()));

  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate("/vehicles"));

  // Araç hiç yüklenemediyse tam sayfa hata; yüklendiyse (hafta geçişi hatası) sayfa kalır, hata üstte görünür
  if (!vehicle) {
    return error ? (
      <div className="fleet-page">
        <div className="fleet-error" role="alert">
          {error}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button className="fleet-btn" type="button" onClick={loadData}>
            {t("fleetUi.retry")}
          </button>
          <button className="fleet-btn secondary" type="button" onClick={goBack}>
            {t("fleetUi.backToVehicles")}
          </button>
        </div>
      </div>
    ) : (
      <div className="fleet-page fleet-empty">{t("common.loading")}</div>
    );
  }

  const imgUrl = vehicleImageUrl(vehicle.image_path);

  return (
    <div className="fleet-page">
      <div className="fleet-page-header">
        <div>
          <h2>{vehicle.name}</h2>
          {vehicle.plate && (
            <div className="subtitle">{vehicle.plate}</div>
          )}
        </div>
        <div className="fleet-header-actions">
          <button
            className="fleet-btn secondary"
            onClick={goBack}
            type="button"
          >
            {t("common.back") || t("common.cancel")}
          </button>
          <button
            className="fleet-btn"
            onClick={() => {
              setInitialStart(null);
              setModalOpen(true);
            }}
            type="button"
          >
            {t("fleet.newReservation")}
          </button>
        </div>
      </div>

      <div className="vehicle-detail">
        <div className="vehicle-info-card">
          {imgUrl ? (
            <img src={imgUrl} alt={vehicle.name} />
          ) : (
            <div
              className="vehicle-card-placeholder"
              style={{ aspectRatio: "16/10" }}
            >
              🚗
            </div>
          )}
          <div className="vehicle-info-body">
            <h3>{vehicle.name}</h3>
            {vehicle.plate && (
              <div className="subtitle">{vehicle.plate}</div>
            )}
          </div>
        </div>

        <div className="week-grid-wrapper">
          <div className="week-grid-toolbar">
            <div className="week-label">{formatWeekLabel(weekStart)}</div>
            <div className="week-nav">
              <input
                type="date"
                className="week-jump"
                aria-label={t("fleetUi.jumpToDate")}
                title={t("fleetUi.jumpToDate")}
                onChange={(e) => {
                  if (!e.target.value) return;
                  setWeekStart(startOfWeek(new Date(`${e.target.value}T00:00:00`)));
                }}
              />
              <button
                className="fleet-btn secondary"
                type="button"
                onClick={prevWeek}
              >
                {t("fleet.prevWeek")}
              </button>
              <button
                className="fleet-btn secondary"
                type="button"
                onClick={thisWeek}
              >
                {t("fleet.thisWeek")}
              </button>
              <button
                className="fleet-btn secondary"
                type="button"
                onClick={nextWeek}
              >
                {t("fleet.nextWeek")}
              </button>
            </div>
          </div>
          {error && (
            <div className="fleet-error" role="alert">
              {error}
            </div>
          )}
          <WeekGrid
            weekStart={weekStart}
            reservations={reservations}
            currentUserId={currentUser?.id}
            onSlotClick={onSlotClick}
            t={t}
          />
        </div>
      </div>

      {modalOpen && currentUser && (
        <ReservationFormModal
          vehicle={vehicle}
          initialStart={initialStart}
          calendarReservations={reservations}
          currentUserId={currentUser.id}
          onClose={() => setModalOpen(false)}
          onCreated={() => {
            setModalOpen(false);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default VehicleDetailPage;
